import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { 
  ChevronDown, 
  ChevronUp,
  Box,
  Filter,
  Gauge,
  Activity,
  Thermometer,
  Timer,
  Waves,
  Zap,
  Beaker,
  Droplets,
  RotateCw
} from 'lucide-react';
import { ProcessFlowDiagram } from './ProcessFlowDiagram';
import { AssetGauge } from './AssetGauge';
import { Asset } from '../types';

export const DeChlorinationStage: React.FC = () => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [selectedAsset, setSelectedAsset] = useState<string | null>(null);

  useEffect(() => {
    const initialAssets: Asset[] = [
      {
        id: 'sodium-dosing',
        name: 'Sodium Bisulfite Dosing',
        type: 'Chemical Dosing System',
        description: 'Dechlorination chemical injection system',
        manufacturer: 'ProMinent',
        model: 'Sigma X Control',
        installationDate: '2023-08-01',
        maintenanceSchedule: 'Monthly',
        status: 'operational',
        metrics: [
          {
            name: 'Dosing Rate',
            value: 12.5,
            unit: 'L/h',
            normalRange: { min: 10, max: 15 },
            criticalRange: { min: 8, max: 17 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 12.5 + Math.random() * 1 - 0.5
            }))
          },
          {
            name: 'Tank Level',
            value: 82,
            unit: '%',
            normalRange: { min: 20, max: 90 },
            criticalRange: { min: 10, max: 95 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 82 + Math.random() * 2 - 1
            }))
          },
          {
            name: 'Solution pH',
            value: 4.2,
            unit: 'pH',
            normalRange: { min: 4.0, max: 4.5 },
            criticalRange: { min: 3.8, max: 4.7 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 4.2 + Math.random() * 0.1 - 0.05
            }))
          }
        ]
      },
      {
        id: 'static-mixer',
        name: 'Static Mixer',
        type: 'Inline Mixer',
        description: 'Chemical mixing and dispersion unit',
        manufacturer: 'Sulzer',
        model: 'CompaX',
        installationDate: '2023-08-05',
        maintenanceSchedule: 'Semi-annual',
        status: 'operational',
        metrics: [
          {
            name: 'Flow Rate',
            value: 175,
            unit: 'm³/h',
            normalRange: { min: 150, max: 200 },
            criticalRange: { min: 130, max: 220 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 175 + Math.random() * 10 - 5
            }))
          },
          {
            name: 'Pressure Drop',
            value: 0.3,
            unit: 'bar',
            normalRange: { min: 0.2, max: 0.4 },
            criticalRange: { min: 0.1, max: 0.5 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 0.3 + Math.random() * 0.04 - 0.02
            }))
          }
        ]
      },
      {
        id: 'chlorine-analyzer',
        name: 'Chlorine Analyzer',
        type: 'Online Analyzer',
        description: 'Residual chlorine monitoring system',
        manufacturer: 'Hach',
        model: 'CL17sc',
        installationDate: '2023-08-10',
        maintenanceSchedule: 'Weekly',
        status: 'warning',
        metrics: [
          {
            name: 'Free Chlorine',
            value: 0.08,
            unit: 'mg/L',
            normalRange: { min: 0, max: 0.1 },
            criticalRange: { min: 0, max: 0.15 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 0.08 + Math.random() * 0.02 - 0.01
            }))
          },
          {
            name: 'Sample Flow',
            value: 0.3,
            unit: 'L/min',
            normalRange: { min: 0.2, max: 0.4 },
            criticalRange: { min: 0.15, max: 0.45 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 0.3 + Math.random() * 0.04 - 0.02
            }))
          },
          {
            name: 'Temperature',
            value: 22,
            unit: '°C',
            normalRange: { min: 15, max: 30 },
            criticalRange: { min: 10, max: 35 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 22 + Math.random() * 1 - 0.5
            }))
          }
        ]
      },
      {
        id: 'contact-tank',
        name: 'Contact Tank',
        type: 'Reaction Tank',
        description: 'Dechlorination reaction vessel',
        manufacturer: 'Custom Built',
        model: 'RT-200',
        installationDate: '2023-08-15',
        maintenanceSchedule: 'Annual',
        status: 'operational',
        metrics: [
          {
            name: 'Level',
            value: 78,
            unit: '%',
            normalRange: { min: 60, max: 90 },
            criticalRange: { min: 50, max: 95 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 78 + Math.random() * 4 - 2
            }))
          },
          {
            name: 'Retention Time',
            value: 15,
            unit: 'min',
            normalRange: { min: 12, max: 18 },
            criticalRange: { min: 10, max: 20 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 15 + Math.random() * 1 - 0.5
            }))
          },
          {
            name: 'ORP',
            value: 250,
            unit: 'mV',
            normalRange: { min: 200, max: 300 },
            criticalRange: { min: 150, max: 350 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 250 + Math.random() * 20 - 10
            }))
          }
        ]
      },
      {
        id: 'outlet-monitor',
        name: 'Outlet Monitor',
        type: 'Quality Monitor',
        description: 'Final water quality monitoring station',
        manufacturer: 'YSI',
        model: 'IQ SensorNet',
        installationDate: '2023-08-20',
        maintenanceSchedule: 'Monthly',
        status: 'operational',
        metrics: [
          {
            name: 'Residual Chlorine',
            value: 0.02,
            unit: 'mg/L',
            normalRange: { min: 0, max: 0.05 },
            criticalRange: { min: 0, max: 0.1 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 0.02 + Math.random() * 0.01 - 0.005
            }))
          },
          {
            name: 'pH',
            value: 7.2,
            unit: 'pH',
            normalRange: { min: 6.8, max: 7.5 },
            criticalRange: { min: 6.5, max: 7.8 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 7.2 + Math.random() * 0.1 - 0.05
            }))
          },
          {
            name: 'Conductivity',
            value: 450,
            unit: 'µS/cm',
            normalRange: { min: 400, max: 500 },
            criticalRange: { min: 350, max: 550 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 450 + Math.random() * 20 - 10
            }))
          }
        ]
      }
    ];

    setAssets(initialAssets);

    const interval = setInterval(() => {
      setAssets(prevAssets => prevAssets.map(asset => ({
        ...asset,
        metrics: asset.metrics.map(metric => {
          const newValue = metric.value + (Math.random() * 2 - 1) * (metric.value * 0.02);
          return {
            ...metric,
            value: newValue,
            history: [
              ...metric.history.slice(1),
              {
                timestamp: Date.now(),
                value: newValue
              }
            ]
          };
        })
      })));
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const getAssetIcon = (assetId: string) => {
    if (assetId.includes('dosing')) return <Beaker className="h-6 w-6" />;
    if (assetId.includes('mixer')) return <RotateCw className="h-6 w-6" />;
    if (assetId.includes('analyzer')) return <Activity className="h-6 w-6" />;
    if (assetId.includes('tank')) return <Box className="h-6 w-6" />;
    if (assetId.includes('monitor')) return <Gauge className="h-6 w-6" />;
    return <Activity className="h-6 w-6" />;
  };

  const getMetricIcon = (metricName: string) => {
    if (metricName.includes('Temperature')) return <Thermometer className="h-4 w-4" />;
    if (metricName.includes('Flow')) return <Waves className="h-4 w-4" />;
    if (metricName.includes('Time')) return <Timer className="h-4 w-4" />;
    if (metricName.includes('Level')) return <Activity className="h-4 w-4" />;
    if (metricName.includes('Pressure')) return <Gauge className="h-4 w-4" />;
    if (metricName.includes('pH') || metricName.includes('ORP') || metricName.includes('Chlorine')) return <Beaker className="h-4 w-4" />;
    if (metricName.includes('Conductivity')) return <Zap className="h-4 w-4" />;
    return <Activity className="h-4 w-4" />;
  };

  const getStatusColor = (status: Asset['status']) => {
    switch (status) {
      case 'operational':
        return 'bg-green-100 text-green-800';
      case 'warning':
        return 'bg-yellow-100 text-yellow-800';
      case 'error':
        return 'bg-red-100 text-red-800';
    }
  };

  const isMetricInRange = (value: number, metric: Asset['metrics'][0]) => {
    if (!metric.normalRange) return true;
    return value >= metric.normalRange.min && value <= metric.normalRange.max;
  };

  return (
    <div className="bg-white rounded-lg shadow-lg overflow-hidden mt-6">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full p-4 bg-teal-600 text-white flex items-center justify-between hover:bg-teal-700 transition-colors"
      >
        <div className="flex items-center space-x-2">
          <Beaker className="h-6 w-6" />
          <h2 className="text-lg font-semibold">Stage 4 - De-Chlorination</h2>
        </div>
        {isExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
      </button>

      {isExpanded && (
        <div className="p-4">
          <div className="mb-6">
            <ProcessFlowDiagram assets={assets} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
            {assets.map(asset => 
              asset.metrics.slice(0, 1).map(metric => (
                <div key={`${asset.id}-${metric.name}`} className="bg-white rounded-lg shadow p-4">
                  <AssetGauge
                    value={metric.value}
                    min={metric.normalRange?.min || 0}
                    max={metric.normalRange?.max || 100}
                    unit={metric.unit}
                    title={`${asset.name} - ${metric.name}`}
                  />
                </div>
              ))
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {assets.map(asset => (
              <div
                key={asset.id}
                onClick={() => setSelectedAsset(asset.id === selectedAsset ? null : asset.id)}
                className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  asset.id === selectedAsset
                    ? 'border-teal-500 bg-teal-50'
                    : 'border-gray-200 hover:border-teal-300'
                }`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-2">
                    {getAssetIcon(asset.id)}
                    <div>
                      <h3 className="font-medium">{asset.name}</h3>
                      <p className="text-sm text-gray-500">{asset.type}</p>
                    </div>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(asset.status)}`}>
                    {asset.status}
                  </span>
                </div>
                <div className="space-y-2">
                  {asset.metrics.map(metric => (
                    <div key={metric.name} className="flex justify-between items-baseline">
                      <div className="flex items-center space-x-2">
                        {getMetricIcon(metric.name)}
                        <span className="text-sm text-gray-500">{metric.name}</span>
                      </div>
                      <span className={`text-lg font-semibold ${
                        isMetricInRange(metric.value, metric) 
                          ? 'text-gray-900' 
                          : 'text-red-600'
                      }`}>
                        {metric.value.toFixed(2)} {metric.unit}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};